import { Children, type ReactNode } from "react";
import { Separator } from "@volca/ui/components/ui/separator";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";

/**
 * Inline metadata ("Version 4 | 8 rounds"): its items in a row with a thin
 * vertical Separator between each two, wrapping onto the next line when the
 * row is narrow. Never a typed dot. A screen reader hears a comma where the
 * line is drawn. Left-out items (null, false, "") take no separator, and
 * neither does an item that renders nothing (a note with nothing to say).
 * Renders spans, so it can sit inside a paragraph.
 */
export const MetaList = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const items = Children.toArray(children).filter((item) => item !== "");
  return (
    <Stack
      render={<span />}
      direction="row"
      align="center"
      wrap
      className={cn("min-w-0 gap-x-2 gap-y-0.5", className)}
    >
      {items.map((item, index) => (
        <span
          key={index}
          className="inline-flex min-w-0 items-center gap-2 has-[>[data-meta-item]:empty]:hidden"
        >
          {index > 0 && (
            <>
              <Separator
                orientation="vertical"
                render={<span />}
                aria-hidden
                // The separator stretches to the top of the line on its own.
                // A short bar has to be centered or it sits above the text.
                className="h-3 self-center!"
              />
              <span className="sr-only">, </span>
            </>
          )}
          <span
            data-meta-item
            className="inline-flex min-w-0 items-center gap-1.5"
          >
            {item}
          </span>
        </span>
      ))}
    </Stack>
  );
};
